/**
 * serviceEditorShared.jsx
 * ─────────────────────────────────────────────────────────
 * Shared helpers used by every Jeyshawn service-detail page:
 *   • ImageUploadField
 *   • ServiceEditModal  (generic: title, paragraphs, list sections, images, extra section)
 *   • usePageEditor     (fetch / save / toast hook — cache-first via localStorage,
 *                         with silent background sync against the DB)
 */
import { useState, useEffect, useRef, useCallback } from "react";

const BASE_URL = "https://api.jeyshawnng.com";
export const UPLOAD_URL = `${BASE_URL}/api/upload`;

export const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BASE_URL}${url}`;
};

/* ─────────────────────────────────────────────
   LOCAL CACHE HELPERS (cache-first, DB-driven)
   One cache slot per apiEndpoint, so /api/csr,
   /api/careers, etc. each get their own key.
───────────────────────────────────────────── */
const cacheKeyFor = (apiEndpoint) => `jeyshawn_page_cache:${apiEndpoint}`;

const getCachedData = (apiEndpoint) => {
  try {
    const raw = localStorage.getItem(cacheKeyFor(apiEndpoint));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setCachedData = (apiEndpoint, data) => {
  try {
    localStorage.setItem(cacheKeyFor(apiEndpoint), JSON.stringify(data));
  } catch {
    // storage full/unavailable — fail silently, page still works
  }
};

/* ═══════════════════════════════
   IMAGE UPLOAD FIELD
═══════════════════════════════ */
export function ImageUploadField({ label, currentUrl, onUploaded }) {
  const [preview, setPreview]     = useState(resolveUrl(currentUrl) || null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState(null);
  const inputRef = useRef();

  useEffect(() => { setPreview(resolveUrl(currentUrl) || null); }, [currentUrl]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res  = await fetch(UPLOAD_URL, { method: "POST", body: formData });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Upload failed");
      onUploaded(json.url);
      setPreview(resolveUrl(json.url));
    } catch (err) {
      setError("Upload failed. Try again.");
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploaded("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="editor-img-field">
      <label>{label}</label>
      {preview ? (
        <div className="editor-img-preview-wrap">
          <img src={preview} alt="preview" className="editor-img-preview" />
          {uploading && (
            <div className="editor-img-uploading">
              <div className="editor-loading__spinner editor-loading__spinner--sm" />
              <span>Uploading...</span>
            </div>
          )}
          {!uploading && (
            <div className="editor-img-actions">
              <button type="button" className="editor-img-btn editor-img-btn--change" onClick={() => inputRef.current?.click()}>Change</button>
              <button type="button" className="editor-img-btn editor-img-btn--remove" onClick={handleRemove}>Remove</button>
            </div>
          )}
        </div>
      ) : (
        <button type="button" className="editor-img-dropzone" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading
            ? <><div className="editor-loading__spinner editor-loading__spinner--sm" /><span>Uploading...</span></>
            : <><span className="editor-img-dropzone__icon">🖼️</span><span>Click to upload image</span><span className="editor-img-dropzone__hint">JPG, PNG, WEBP — max 10 MB</span></>
          }
        </button>
      )}
      {error && <p className="editor-img-error">{error}</p>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}

/* ═══════════════════════════════
   GENERIC SERVICE EDIT MODAL
   ─────────────────────────────
   data shape expected:
   {
     bannerImage : string,
     bgImage     : string,   // optional — section background
     title       : string,
     paragraphs  : string[], // ordered intro paragraphs
     sections    : [{ heading: string, items: string[] }],
     extraSection?: {                    // optional — one extra heading+paragraphs+bullets block
       heading: string,
       paragraphs: string[],
       bulletHeading: string,
       bulletItems: string[],
     },
     // any keys named in extraFields / extraImageFields (see below)
   }

   extraFields: [{ key, label, rows }]
     → renders a plain textarea bound to draft[key] (string field).

   extraImageFields: [{ key, label }]
     → renders an ImageUploadField bound to draft[key] (string URL field).
═══════════════════════════════ */
export function ServiceEditModal({
  data,
  onSave,
  onClose,
  saving,
  pageLabel,
  extraFields = [],
  extraImageFields = [],
}) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(data)));

  const setPara    = (i, v) => setDraft(d => { const p = [...d.paragraphs]; p[i] = v; return { ...d, paragraphs: p }; });
  const addPara    = ()     => setDraft(d => ({ ...d, paragraphs: [...d.paragraphs, ""] }));
  const removePara = (i)    => setDraft(d => ({ ...d, paragraphs: d.paragraphs.filter((_, idx) => idx !== i) }));

  const setSectionHeading = (si, v)     => setDraft(d => { const s = [...d.sections]; s[si] = { ...s[si], heading: v }; return { ...d, sections: s }; });
  const setSectionItem    = (si, ii, v) => setDraft(d => { const s = [...d.sections]; const items = [...s[si].items]; items[ii] = v; s[si] = { ...s[si], items }; return { ...d, sections: s }; });
  const addSectionItem    = (si)        => setDraft(d => { const s = [...d.sections]; s[si] = { ...s[si], items: [...s[si].items, ""] }; return { ...d, sections: s }; });
  const removeSectionItem = (si, ii)    => setDraft(d => { const s = [...d.sections]; s[si] = { ...s[si], items: s[si].items.filter((_, idx) => idx !== ii) }; return { ...d, sections: s }; });
  const addSection        = ()          => setDraft(d => ({ ...d, sections: [...d.sections, { heading: "New Section", items: [""] }] }));
  const removeSection     = (si)        => setDraft(d => ({ ...d, sections: d.sections.filter((_, idx) => idx !== si) }));

  // ── EXTRA SECTION handlers ──
  const setExtraHeading = (v) => setDraft(d => ({ ...d, extraSection: { ...d.extraSection, heading: v } }));
  const setExtraPara    = (i, v) => setDraft(d => { const p = [...d.extraSection.paragraphs]; p[i] = v; return { ...d, extraSection: { ...d.extraSection, paragraphs: p } }; });
  const addExtraPara    = () => setDraft(d => ({ ...d, extraSection: { ...d.extraSection, paragraphs: [...d.extraSection.paragraphs, ""] } }));
  const removeExtraPara = (i) => setDraft(d => ({ ...d, extraSection: { ...d.extraSection, paragraphs: d.extraSection.paragraphs.filter((_, idx) => idx !== i) } }));

  const setExtraBulletHeading = (v) => setDraft(d => ({ ...d, extraSection: { ...d.extraSection, bulletHeading: v } }));
  const setExtraBulletItem    = (i, v) => setDraft(d => { const items = [...d.extraSection.bulletItems]; items[i] = v; return { ...d, extraSection: { ...d.extraSection, bulletItems: items } }; });
  const addExtraBulletItem    = () => setDraft(d => ({ ...d, extraSection: { ...d.extraSection, bulletItems: [...d.extraSection.bulletItems, ""] } }));
  const removeExtraBulletItem = (i) => setDraft(d => ({ ...d, extraSection: { ...d.extraSection, bulletItems: d.extraSection.bulletItems.filter((_, idx) => idx !== i) } }));

  const addExtraSection    = () => setDraft(d => ({ ...d, extraSection: { heading: "New Section", paragraphs: [""], bulletHeading: "Includes:", bulletItems: [""] } }));
  const removeExtraSection = () => setDraft(d => { const { extraSection, ...rest } = d; return rest; });

  // ── GENERIC extraFields / extraImageFields handlers ──
  const setExtraFieldValue = (key, v) => setDraft(d => ({ ...d, [key]: v }));
  const setExtraImageValue = (key, url) => setDraft(d => ({ ...d, [key]: url }));

  return (
    <div className="editor-overlay" onClick={onClose}>
      <div className="editor-modal" onClick={e => e.stopPropagation()}>

        <div className="editor-modal__header">
          <h2>Edit — {pageLabel}</h2>
          <button className="editor-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="editor-modal__body">

          {/* PAGE IMAGES */}
          <p className="editor-section__title">Page Images</p>
          <div className="editor-group">
            <ImageUploadField
              label="Banner Image (top of page)"
              currentUrl={draft.bannerImage}
              onUploaded={url => setDraft(d => ({ ...d, bannerImage: url }))}
            />
            {"bgImage" in draft && (
              <ImageUploadField
                label="Section Background Image"
                currentUrl={draft.bgImage}
                onUploaded={url => setDraft(d => ({ ...d, bgImage: url }))}
              />
            )}

            {/* extraImageFields — additional named image fields (e.g. secondaryImage) */}
            {extraImageFields.map(({ key, label }) => (
              <ImageUploadField
                key={key}
                label={label}
                currentUrl={draft[key]}
                onUploaded={url => setExtraImageValue(key, url)}
              />
            ))}
          </div>

          {/* MAIN TITLE */}
          <p className="editor-section__title">Main Content</p>
          <div className="editor-group">
            <label>Title / Heading</label>
            <textarea rows={3} value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} />
          </div>

          {/* extraFields — additional named text/textarea fields (e.g. commitmentLabel, closingText) */}
          {extraFields.length > 0 && (
            <div className="editor-group">
              {extraFields.map(({ key, label, rows = 2 }) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <label>{label}</label>
                  <textarea
                    rows={rows}
                    value={draft[key] ?? ""}
                    onChange={e => setExtraFieldValue(key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          )}

          {/* PARAGRAPHS */}
          <p className="editor-section__title">Paragraphs</p>
          <div className="editor-group">
            <p className="editor-group__label">Intro Paragraphs (displayed in order)</p>
            {draft.paragraphs.map((para, i) => (
              <div key={i} className="editor-value-row">
                <textarea
                  rows={4}
                  value={para}
                  onChange={e => setPara(i, e.target.value)}
                  style={{ flex: 1, padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
                />
                <button className="editor-value-row__remove" onClick={() => removePara(i)} title="Remove paragraph">✕</button>
              </div>
            ))}
            <button className="editor-add-btn" onClick={addPara}>+ Add Paragraph</button>
          </div>

          {/* BULLET SECTIONS */}
          {draft.sections.length > 0 && (
            <>
              <p className="editor-section__title">Bullet Sections</p>
              {draft.sections.map((sec, si) => (
                <div key={si} className="editor-group">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <p className="editor-group__label">Section {si + 1}</p>
                    <button className="editor-value-row__remove" onClick={() => removeSection(si)} title="Remove section" style={{ marginBottom: 4 }}>✕</button>
                  </div>
                  <label>Section Heading</label>
                  <input value={sec.heading} onChange={e => setSectionHeading(si, e.target.value)} />
                  <label>List Items</label>
                  {sec.items.map((item, ii) => (
                    <div key={ii} className="editor-value-row">
                      <input value={item} onChange={e => setSectionItem(si, ii, e.target.value)} />
                      <button className="editor-value-row__remove" onClick={() => removeSectionItem(si, ii)} title="Remove item">✕</button>
                    </div>
                  ))}
                  <button className="editor-add-btn" onClick={() => addSectionItem(si)}>+ Add Item</button>
                </div>
              ))}
            </>
          )}
          <button className="editor-add-btn" onClick={addSection} style={{ marginTop: draft.sections.length > 0 ? 0 : 0 }}>
            + Add Bullet Section
          </button>

          {/* EXTRA SECTION (heading + paragraphs + its own bullet list) */}
          <p className="editor-section__title" style={{ marginTop: 20 }}>Extra Section</p>
          {draft.extraSection ? (
            <div className="editor-group">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <p className="editor-group__label">Section Heading</p>
                <button className="editor-value-row__remove" onClick={removeExtraSection} title="Remove extra section" style={{ marginBottom: 4 }}>✕</button>
              </div>
              <input value={draft.extraSection.heading} onChange={e => setExtraHeading(e.target.value)} />

              <label style={{ marginTop: 10, display: "block" }}>Paragraphs</label>
              {draft.extraSection.paragraphs.map((para, i) => (
                <div key={i} className="editor-value-row">
                  <textarea
                    rows={4}
                    value={para}
                    onChange={e => setExtraPara(i, e.target.value)}
                    style={{ flex: 1, padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
                  />
                  <button className="editor-value-row__remove" onClick={() => removeExtraPara(i)} title="Remove paragraph">✕</button>
                </div>
              ))}
              <button className="editor-add-btn" onClick={addExtraPara}>+ Add Paragraph</button>

              <label style={{ marginTop: 14, display: "block" }}>Bullet List Heading</label>
              <input value={draft.extraSection.bulletHeading} onChange={e => setExtraBulletHeading(e.target.value)} />

              <label style={{ marginTop: 10, display: "block" }}>Bullet Items</label>
              {draft.extraSection.bulletItems.map((item, i) => (
                <div key={i} className="editor-value-row">
                  <input value={item} onChange={e => setExtraBulletItem(i, e.target.value)} />
                  <button className="editor-value-row__remove" onClick={() => removeExtraBulletItem(i)} title="Remove item">✕</button>
                </div>
              ))}
              <button className="editor-add-btn" onClick={addExtraBulletItem}>+ Add Bullet Item</button>
            </div>
          ) : (
            <button className="editor-add-btn" onClick={addExtraSection}>+ Add Extra Section</button>
          )}

        </div>

        <div className="editor-modal__footer">
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn--orange" onClick={() => onSave(draft)} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

      </div>
    </div>
  );
}


/**
 * usePageEditor
 * ─────────────────────────────────────────────
 * Cache-first: on mount, if a cached copy exists for this apiEndpoint,
 * pageData is populated instantly from localStorage (no loading spinner,
 * no re-render flash), then a silent background fetch checks the DB and
 * only updates state + cache if the DB data actually changed.
 *
 * If nothing is cached yet, it fetches in the foreground (loading = true)
 * same as before.
 *
 * A background sync also runs on a 30s interval so changes made elsewhere
 * (another tab, another admin) show up without a manual refresh.
 */
export function usePageEditor(apiEndpoint, defaultData) {
  const cached = getCachedData(apiEndpoint);

  const [pageData, setPageData]   = useState(cached ?? defaultData);
  const [loading, setLoading]     = useState(!cached);
  const [fetchErr, setFetchErr]   = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState(null);

  const fetchData = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoading(true);
        setFetchErr(null);
      }

      const res  = await fetch(`${BASE_URL}${apiEndpoint}`);
      const json = await res.json();

      if (json.success) {
        const prevCached = getCachedData(apiEndpoint);
        const changed = JSON.stringify(prevCached) !== JSON.stringify(json.data);
        if (changed) {
          setPageData(json.data);
          setCachedData(apiEndpoint, json.data);
        }
      } else {
        throw new Error(json.message || "Failed to fetch");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      // Background syncs, or foreground loads when we already have cached
      // data, fail silently so the page keeps showing what it has.
      if (!isBackground && !getCachedData(apiEndpoint)) {
        setFetchErr("Could not load page data.");
      }
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [apiEndpoint]);

  useEffect(() => {
    const hasCache = !!getCachedData(apiEndpoint);
    fetchData(hasCache); // silent sync if we already have cached data to show

    const interval = setInterval(() => fetchData(true), 30000); // auto-check DB every 30s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiEndpoint]);

  const handleSave = async (draft) => {
    try {
      setSaving(true);

      const res = await fetch(`${BASE_URL}${apiEndpoint}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });

      const json = await res.json();

      if (!json.success) {
        throw new Error(json.message || "Save failed");
      }

      setPageData(json.data);
      setCachedData(apiEndpoint, json.data);
      setModalOpen(false);
      showToast("Changes saved successfully!");
    } catch (err) {
      console.error("Save error:", err);
      showToast("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const showToast = (msg) => { setSaveMsg(msg); setTimeout(() => setSaveMsg(null), 3500); };

  return { pageData, loading, fetchErr, fetchData: () => fetchData(false), modalOpen, setModalOpen, saving, handleSave, saveMsg };
}